/**
 * Template registry — DEC-0409 step 3 (docs/specs/universal-instance-contract.md §4).
 *
 * A template is canonical and migrated: it lives in entity_templates /
 * entity_template_versions, not in browser JSON. It may declare the allowed
 * lenses, the field order, the actions, the layout and the defaults — exactly
 * the shape TemplateConfig already describes, so nothing new is invented here.
 *
 * Three rules from §4 are enforced by this module and nowhere else:
 *
 *  1. The link between an instance and a template is a relation with a pinned
 *     version (vb_record_template_links.version_id), so resolution NEVER reads
 *     the template's current active version. A published template change is
 *     visible as 'pending_review' and reaches the instance only when someone
 *     approves it — locked decision 2026-09-09: template changes never
 *     auto-propagate.
 *  2. An instance delta is additive only. It may append fields or sections the
 *     template does not declare; it may never redefine what the template does
 *     declare, and it never touches another instance. Rejected keys are
 *     returned rather than silently dropped, so a screen can explain itself.
 *  3. Content and template stay separable: the delta rides in
 *     vb_records.meta.__template_delta and the pinned version in the link row,
 *     so removing the link leaves the record's content untouched.
 *
 * Pure and deterministic: no network, no database. Callers fetch the rows and
 * pass them in, the same contract as the step-1 instance resolver.
 *
 * Compatibility path: 21 entity types carry a legacy entity_types.template_config
 * today. Until a template row exists for a type, resolution falls back to that
 * jsonb and reports source 'entity_type'. No data is copied, nothing is written.
 */

import type { EntityType, I18nLabel, TemplateConfig, TemplateSection, ViewType } from './types';

export type TemplateVersionStatus = 'draft' | 'published' | 'archived';

/** A registry row: which template, for which type, in which workspace. */
export interface EntityTemplate {
  id: string;
  workspaceId: string;
  entityTypeId: string;
  slug: string;
  name: I18nLabel;
  isDefault: boolean;
  /** The newest published version. Never resolved directly — see rule 1. */
  activeVersionId: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** One frozen definition of a template. Versions are immutable once published. */
export interface EntityTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  status: TemplateVersionStatus;
  config: TemplateConfig;
  /** What changed, in the author's words — the review screen shows it. */
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  publishedAt: string | null;
}

/** The relation row: this instance follows this template at this version. */
export interface InstanceTemplateLink {
  id: string;
  recordId: string;
  templateId: string;
  versionId: string;
  pinnedAt: string;
  /** Who approved the pinned version onto this instance. */
  approvedBy: string | null;
}

/** Where the resolved definition came from. */
export type TemplateSource =
  /** entity_templates + the pinned entity_template_versions row. */
  | 'registry'
  /** Legacy entity_types.template_config, until the type has a template row. */
  | 'entity_type'
  /** Neither — the instance renders with no template. */
  | 'none';

/** Whether the instance is behind its template. Drives the review-and-approve screen. */
export type TemplateUpdateState =
  /** The pinned version is the template's active version. */
  | 'current'
  /** The template published a newer version. Waits for approval — never applied here. */
  | 'pending_review'
  /** The instance follows no template row at all. */
  | 'unlinked'
  /** The template has no active version, or the link points outside it. */
  | 'detached';

export interface InstanceTemplateDelta {
  /** Field refs appended after the template's own order. */
  extra_field_refs?: string[];
  /** Sections appended after the template's own sections. */
  extra_sections?: TemplateSection[];
  /** Presentation only — which sections this instance opens collapsed. */
  collapsed_sections?: string[];
}

export interface AppliedDelta {
  /** Delta keys that were honoured. */
  applied: string[];
  /** Delta keys refused because the template already declares them (rule 2). */
  rejected: string[];
  /** Section keys this instance opens collapsed. Presentation, not definition. */
  collapsedSections: string[];
}

export interface ResolvedTemplate {
  source: TemplateSource;
  templateId: string | null;
  versionId: string | null;
  version: number | null;
  /** The definition the lens renders: template config plus additive delta. */
  config: TemplateConfig | null;
  delta: AppliedDelta;
  updateState: TemplateUpdateState;
}

/** Where an instance keeps its own delta. One meta key, no column, no table. */
export const INSTANCE_TEMPLATE_DELTA_KEY = '__template_delta';

/** Everything a delta is allowed to name. Anything else is rejected. */
export const INSTANCE_DELTA_KEYS: readonly string[] = [
  'extra_field_refs',
  'extra_sections',
  'collapsed_sections',
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function sectionArray(v: unknown): TemplateSection[] {
  if (!Array.isArray(v)) return [];
  return v.filter((s): s is TemplateSection =>
    isRecord(s) && typeof s.key === 'string' && isRecord(s.label) && Array.isArray(s.field_refs));
}

/**
 * Tolerant reader for a stored config (registry jsonb or legacy template_config).
 * Missing branches get the same defaults defaultTemplateConfig() hands out, so a
 * half-filled row never crashes a lens. Returns null only for a non-object.
 */
export function normalizeTemplateConfig(raw: unknown): TemplateConfig | null {
  if (!isRecord(raw)) return null;
  const layout = isRecord(raw.layout) ? raw.layout : {};
  const metaColumns = layout.meta_columns;
  return {
    ...raw,
    layout: {
      meta_columns: metaColumns === 1 || metaColumns === 2 || metaColumns === 3 ? metaColumns : 2,
      field_order: stringArray(layout.field_order),
      sections: sectionArray(layout.sections),
    },
    available_views: (Array.isArray(raw.available_views) ? raw.available_views : []) as ViewType[],
    track_activity: raw.track_activity === true,
    track_kpi_events: raw.track_kpi_events === true,
  } as TemplateConfig;
}

/** The delta an instance carries, read defensively from vb_records.meta. */
export function readInstanceTemplateDelta(
  meta: Record<string, unknown> | null | undefined,
): InstanceTemplateDelta | null {
  const raw = meta?.[INSTANCE_TEMPLATE_DELTA_KEY];
  return isRecord(raw) ? (raw as InstanceTemplateDelta) : null;
}

const EMPTY_DELTA: AppliedDelta = { applied: [], rejected: [], collapsedSections: [] };

/**
 * Additive-only merge (rule 2). A field ref the template already orders, or a
 * section key it already declares, is refused — the instance cannot redefine
 * the template, only extend its own copy of the rendering.
 */
export function applyInstanceDelta(
  config: TemplateConfig,
  delta: InstanceTemplateDelta | null | undefined,
): { config: TemplateConfig; delta: AppliedDelta } {
  if (!delta || !isRecord(delta)) return { config, delta: { ...EMPTY_DELTA } };

  const applied: string[] = [];
  const rejected: string[] = [];

  // A key the delta names but the allowlist does not is out of bounds by name
  // alone — no need to look at the template to refuse it.
  for (const key of Object.keys(delta)) {
    if (!INSTANCE_DELTA_KEYS.includes(key)) rejected.push(key);
  }

  const ownedRefs = new Set([
    ...config.layout.field_order,
    ...config.layout.sections.flatMap(s => s.field_refs),
  ]);
  const ownedSections = new Set(config.layout.sections.map(s => s.key));

  const extraRefs = stringArray(delta.extra_field_refs).filter(ref => {
    if (ownedRefs.has(ref)) return false;
    ownedRefs.add(ref);
    return true;
  });
  const extraSections = sectionArray(delta.extra_sections).filter(s => {
    if (ownedSections.has(s.key)) return false;
    ownedSections.add(s.key);
    return true;
  });

  if (delta.extra_field_refs !== undefined) {
    (extraRefs.length > 0 ? applied : rejected).push('extra_field_refs');
  }
  if (delta.extra_sections !== undefined) {
    (extraSections.length > 0 ? applied : rejected).push('extra_sections');
  }

  const collapsedSections = stringArray(delta.collapsed_sections);
  if (delta.collapsed_sections !== undefined) applied.push('collapsed_sections');

  if (extraRefs.length === 0 && extraSections.length === 0) {
    return { config, delta: { applied, rejected, collapsedSections } };
  }
  return {
    config: {
      ...config,
      layout: {
        ...config.layout,
        field_order: [...config.layout.field_order, ...extraRefs],
        sections: [...config.layout.sections, ...extraSections],
      },
    },
    delta: { applied, rejected, collapsedSections },
  };
}

/** Is the instance behind its template? Reported, never acted on (rule 1). */
export function templateUpdateState(
  link: InstanceTemplateLink | null | undefined,
  template: EntityTemplate | null | undefined,
): TemplateUpdateState {
  if (!link || !template) return 'unlinked';
  if (link.templateId !== template.id || !template.activeVersionId) return 'detached';
  return link.versionId === template.activeVersionId ? 'current' : 'pending_review';
}

export interface ResolveTemplateInput {
  /** The type row, for the legacy template_config fallback. */
  entityType: EntityType | null;
  /** The linked template row, when the instance follows one. */
  template?: EntityTemplate | null;
  /** The version the link pins — NOT the template's active version. */
  pinnedVersion?: EntityTemplateVersion | null;
  link?: InstanceTemplateLink | null;
  /** vb_records.meta of the instance; the delta is read from it. */
  meta?: Record<string, unknown> | null;
}

/**
 * The one answer to "which template does this instance render with".
 *
 * Resolution order: the pinned version, then the type's legacy template_config,
 * then nothing. The template's active version is looked at only to report
 * updateState — a newer published version changes nothing on screen until it is
 * approved onto the instance.
 */
export function resolveInstanceTemplate(input: ResolveTemplateInput): ResolvedTemplate {
  const { entityType, template, pinnedVersion, link, meta } = input;
  const delta = readInstanceTemplateDelta(meta);
  const updateState = templateUpdateState(link, template);

  // The link must point at the version it was handed: a mismatch means the rows
  // were fetched inconsistently, and guessing would silently propagate.
  const pinned =
    link && pinnedVersion && pinnedVersion.id === link.versionId && pinnedVersion.templateId === link.templateId
      ? pinnedVersion
      : null;

  const registryConfig = pinned ? normalizeTemplateConfig(pinned.config) : null;
  if (pinned && registryConfig) {
    const merged = applyInstanceDelta(registryConfig, delta);
    return {
      source: 'registry',
      templateId: pinned.templateId,
      versionId: pinned.id,
      version: pinned.version,
      config: merged.config,
      delta: merged.delta,
      updateState,
    };
  }

  const legacy = normalizeTemplateConfig(entityType?.template_config ?? null);
  if (legacy) {
    const merged = applyInstanceDelta(legacy, delta);
    return {
      source: 'entity_type',
      templateId: null,
      versionId: null,
      version: null,
      config: merged.config,
      delta: merged.delta,
      updateState,
    };
  }

  return {
    source: 'none',
    templateId: null,
    versionId: null,
    version: null,
    config: null,
    delta: { ...EMPTY_DELTA },
    updateState,
  };
}

export type TemplateChangeKind = 'added' | 'removed' | 'changed';

export interface TemplateChange {
  /** Dotted path inside TemplateConfig, e.g. layout.sections.finance */
  path: string;
  kind: TemplateChangeKind;
}

function sortedKeys(o: Record<string, unknown>): string[] {
  return Object.keys(o).sort();
}

/**
 * What a review-and-approve screen shows before a newer version is pinned onto
 * an instance. Compares the pinned config with the candidate one, one level of
 * TemplateConfig plus the two lists people actually argue about — the field
 * order and the sections.
 */
export function diffTemplateConfigs(
  pinned: TemplateConfig | null,
  candidate: TemplateConfig | null,
): TemplateChange[] {
  if (!pinned || !candidate) return [];
  const changes: TemplateChange[] = [];

  const before = pinned as unknown as Record<string, unknown>;
  const after = candidate as unknown as Record<string, unknown>;
  for (const key of [...new Set([...sortedKeys(before), ...sortedKeys(after)])]) {
    if (key === 'layout') continue;
    const inBefore = key in before;
    const inAfter = key in after;
    if (inBefore && !inAfter) changes.push({ path: key, kind: 'removed' });
    else if (!inBefore && inAfter) changes.push({ path: key, kind: 'added' });
    else if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      changes.push({ path: key, kind: 'changed' });
    }
  }

  if (pinned.layout.meta_columns !== candidate.layout.meta_columns) {
    changes.push({ path: 'layout.meta_columns', kind: 'changed' });
  }
  if (JSON.stringify(pinned.layout.field_order) !== JSON.stringify(candidate.layout.field_order)) {
    changes.push({ path: 'layout.field_order', kind: 'changed' });
  }

  const beforeSections = new Map(pinned.layout.sections.map(s => [s.key, s]));
  const afterSections = new Map(candidate.layout.sections.map(s => [s.key, s]));
  for (const key of [...new Set([...beforeSections.keys(), ...afterSections.keys()])]) {
    const b = beforeSections.get(key);
    const a = afterSections.get(key);
    if (b && !a) changes.push({ path: `layout.sections.${key}`, kind: 'removed' });
    else if (!b && a) changes.push({ path: `layout.sections.${key}`, kind: 'added' });
    else if (JSON.stringify(b) !== JSON.stringify(a)) {
      changes.push({ path: `layout.sections.${key}`, kind: 'changed' });
    }
  }

  return changes;
}

/** The next version number for a template. Versions are dense and 1-based. */
export function nextTemplateVersionNumber(versions: readonly EntityTemplateVersion[]): number {
  return versions.reduce((max, v) => Math.max(max, v.version), 0) + 1;
}

/** The default template of a type, when the workspace declared one. */
export function pickDefaultTemplate(
  templates: readonly EntityTemplate[],
  entityTypeId: string,
): EntityTemplate | null {
  const ofType = templates.filter(t => t.entityTypeId === entityTypeId);
  return ofType.find(t => t.isDefault) ?? ofType[0] ?? null;
}
